import { useEffect, useState } from "react";
import styles from "./CommunityContentPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import useAuthStore from "../../store/authStore";
import useDashboardStore from "../../store/dashboardStore";
import videoPlaceholderImage from "../../assets/images/video-player-placeholder.png";
import { FaSpinner } from "react-icons/fa";
import PageContentSections, { toSections } from "./PageContentSections";

const formatBoldText = (text = "") =>
  text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

const isMobileApp = () => /flutter|webview/i.test(navigator.userAgent);

const getYoutubeThumbnail = (url) => {
  const match = url.match(/(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};
const checkVideoAccess = (
  membershipExpiry,
  userMembershipIds,
  isAdmin,
  hasActiveIap,
) => {
  if (isAdmin) return true;
  if (!membershipExpiry || membershipExpiry.length === 0) return true; // no expiry = free

  const now = new Date();

  for (const entry of membershipExpiry) {
    const { membership_id, expires_at } = entry;

    // membership_id 0 = free tier
    if (membership_id === 0) {
      if (expires_at === null) return true; // lifetime free
      if (new Date(expires_at) > now) return true; // free and not expired
      continue; // free but expired — keep checking paid
    }

    // Paid membership entry
    if (hasActiveIap) return true; // IAP covers all paid plans

    if (userMembershipIds.includes(Number(membership_id))) {
      if (expires_at === null) return true; // lifetime paid
      if (new Date(expires_at) > now) return true; // paid and not expired
    }
  }

  return false;
};

const getVimeoThumbnail = async (url) => {
  const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
  const match = url.match(regex);
  if (!match || !match[1]) return null;
  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`,
    );
    if (response.ok) {
      const data = await response.json();
      if (data.thumbnail_url) return data.thumbnail_url;
    }
  } catch (error) {
    console.error("Vimeo API error:", error);
  }
  return `https://vumbnail.com/${match[1]}.jpg`;
};

const getVideoThumbnail = async (url) => {
  if (url.includes("vimeo")) return await getVimeoThumbnail(url);
  if (url.includes("youtube") || url.includes("youtu.be"))
    return getYoutubeThumbnail(url);
  return videoPlaceholderImage;
};

const ReplaySessionsPage = () => {
  const { communityIndex, communities } = useDashboardStore();
  const { user, userMeta, refreshIapStatus } = useAuthStore();

  const [contentData, setContentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [thumbnailCache, setThumbnailCache] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const community = communities[communityIndex];
  const communityId = community?.id;
  const role = user?.roles?.[0];

  let userMembershipIds = [];
  if (Array.isArray(userMeta?.membership_ids)) {
    userMembershipIds = userMeta.membership_ids.map(Number);
  } else if (typeof userMeta?.membership_ids === "string") {
    try {
      userMembershipIds = JSON.parse(userMeta.membership_ids).map(Number);
    } catch {
      userMembershipIds = [];
    }
  }

  useEffect(() => {
    const fetchContent = async () => {
      if (!communityId) {
        setIsLoading(false);
        setContentData(null);
        return;
      }
      setIsLoading(true);
      try {
        const { data } = await api.get(
          ENDPOINTS.GET_PAGE_CONTENT(communityId, "replaysessions"),
        );
        if (data.success && data.content) {
          setContentData(data.content);
        } else {
          setContentData(null);
        }
      } catch (error) {
        console.error("Error fetching replay sessions content:", error);
        toast.error("Failed to load replay sessions content");
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, [communityId]);

  useEffect(() => {
    refreshIapStatus();
  }, []);

  const sections = toSections(contentData);

  useEffect(() => {
    const secs = toSections(contentData);
    if (secs.length === 0) return;

    const load = async () => {
      const cache = {};
      for (const sec of secs) {
        for (const tab of sec.video_tabs || []) {
          for (const group of tab.groups || []) {
            for (const video of group.videos || []) {
              if (!video.cover_image && video.url) {
                const key = video.id || video.url;
                cache[key] =
                  (await getVideoThumbnail(video.url)) || videoPlaceholderImage;
              }
            }
          }
        }
      }
      setThumbnailCache(cache);
    };
    load();
  }, [contentData]);

  const handleMembershipClick = async (membershipExpiry) => {
    if (isPurchasing || isLoadingPlans) return;
    setIsLoadingPlans(true);
    try {
      const paidIds = (membershipExpiry || [])
        .filter((e) => e.membership_id !== 0)
        .map((e) => e.membership_id);

      if (!paidIds.length) {
        toast.error("No membership plans configured for this video");
        return;
      }
      if (paidIds.length === 1) {
        handlePurchaseMembership(paidIds[0]);
      } else {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS, {
          params: { hide_restricted: true },
        });
        const allPlans = data.memberships || [];
        setAvailablePlans(allPlans.filter((plan) => paidIds.includes(plan.id)));
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error loading membership plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;
    setIsPurchasing(true);
    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });
      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link",
        );
      }
    } catch (error) {
      toast.error("Failed to initiate purchase");
    } finally {
      setIsPurchasing(false);
    }
  };

  const checkTraderAccessToCommunity = async () => {
    try {
      const { channels } = useDashboardStore.getState();
      const communityChannels = channels.filter(
        (channel) => channel.community === community?.name,
      );
      if (communityChannels.length === 0) return false;
      const channelIds = communityChannels.map((c) => c.id);
      const { data } = await api.post(ENDPOINTS.GET_CHANNELS_BY_IDS, {
        channelIds,
      });
      if (data.success) {
        return (data.channels || []).some((channel) =>
          (channel.trader_ids || []).includes(user.id),
        );
      }
      return false;
    } catch (error) {
      console.error("Error checking trader access:", error);
      return false;
    }
  };

  /* ---------------- VIDEO CLICK ---------------- */

  const handleAttachmentClick = async (e, fileUrl, video) => {
    let hasAccess = true;

    if (video.membership_ids?.length) {
      if (role === "administrator") {
        hasAccess = true;
      } else if (role === "trader") {
        hasAccess = await checkTraderAccessToCommunity();
      } else {
        if (userMeta?.has_active_iap === true) {
          hasAccess = true;
        } else {
          const normalizedVideoMembershipIds = (video.membership_ids || [])
            .map((id) => {
              const num = Number(id);
              return isNaN(num) ? 0 : num;
            })
            .filter((id) => id > 0);

          hasAccess = normalizedVideoMembershipIds.some((id) =>
            userMembershipIds.includes(id),
          );
        }
      }
    }

    if (!hasAccess) {
      toast.error("File is restricted");
      handleMembershipClick(video.membership_ids);
      return;
    } else {
      e.stopPropagation();
      window.open(fileUrl, "_blank");
    }
  };

  const handleVideoClick = async (video) => {
    const isAdmin = role === "administrator";
    const hasActiveIap = userMeta?.has_active_iap === true;

    let hasAccess;

    if (isAdmin) {
      hasAccess = true;
    } else if (role === "trader") {
      // Check membership first
      const expiry = video.membership_expiry || [];
      const ids = video.membership_ids || [];

      if (expiry.length > 0) {
        hasAccess = checkVideoAccess(
          expiry,
          userMembershipIds,
          isAdmin,
          hasActiveIap,
        );
      } else if (ids.length > 0) {
        const paidIds = ids.map(Number).filter((id) => id > 0);
        hasAccess =
          hasActiveIap || paidIds.some((id) => userMembershipIds.includes(id));
      } else {
        hasAccess = true;
      }

      // Fall back to channel assignment check
      if (!hasAccess) {
        hasAccess = await checkTraderAccessToCommunity();
      }
    } else if (video.membership_expiry && video.membership_expiry.length > 0) {
      hasAccess = checkVideoAccess(
        video.membership_expiry,
        userMembershipIds,
        isAdmin,
        hasActiveIap,
      );
    } else if (video.membership_ids?.length) {
      if (hasActiveIap) {
        hasAccess = true;
      } else {
        const paidIds = (video.membership_ids || [])
          .map(Number)
          .filter((id) => id > 0);
        hasAccess = paidIds.some((id) => userMembershipIds.includes(id));
      }
    } else {
      hasAccess = true;
    }

    if (!hasAccess) {
      toast.error("Video is restricted");
      handleMembershipClick(video.membership_expiry || []);
      return;
    }

    if (video.url.includes("vimeo")) {
      const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
      const match = video.url.match(regex);
      if (match && match[1]) {
        setSelectedVideo({ type: "vimeo", id: match[1], hash: match[2] });
        setShowVideoModal(true);
      }
    } else {
      window.open(video.url, "_blank", "noopener,noreferrer");
    }
  };
  const hasContent = sections.length > 0;

  if (isLoading) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>Replay Sessions</h2>
        </div>
        <div className={styles.scrollableContent}>
          <div style={{ textAlign: "center", padding: "50px" }}>
            <FaSpinner className="fa-spin" style={{ fontSize: "48px" }} />
            <p style={{ marginTop: "20px" }}>Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>
          {contentData?.academy_title || "Replay Sessions"}
        </h2>
      </div>

      <div className={styles.scrollableContent}>
        {!hasContent && <p>No Replay Sessions available for this community</p>}

        <PageContentSections
          sections={sections}
          thumbnailCache={thumbnailCache}
          role={role}
          userMembershipIds={userMembershipIds}
          onVideoClick={handleVideoClick}
          onAttachmentClick={handleAttachmentClick}
        />
      </div>

      {/* Plan Selection Modal */}
      {showPlanSelection && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupContent}>
            <h3>Choose a Membership Plan</h3>
            {availablePlans.map((plan) => (
              <div key={plan.id} style={{ marginBottom: 16 }}>
                <h4>{plan.name || plan.label}</h4>
                <p>
                  {plan.payment_type === "free" ? "Free" : `$${plan.price}`}
                </p>
                <button
                  className={styles.popupButton}
                  disabled={isPurchasing}
                  onClick={() => {
                    handlePurchaseMembership(plan.id);
                    setShowPlanSelection(false);
                  }}
                >
                  {isPurchasing ? (
                    <>
                      <FaSpinner className="fa-spin" /> Processing...
                    </>
                  ) : (
                    "Select Plan"
                  )}
                </button>
              </div>
            ))}
            <button
              className={styles.popupButton}
              onClick={() => setShowPlanSelection(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Vimeo Modal */}
      {showVideoModal && selectedVideo && (
        <div
          className={styles.videoModalOverlay}
          onClick={() => {
            setShowVideoModal(false);
            setSelectedVideo(null);
          }}
        >
          <div
            className={styles.videoModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowVideoModal(false);
                setSelectedVideo(null);
              }}
            >
              ✕
            </button>
            {selectedVideo.type === "vimeo" && (
              <iframe
                src={
                  selectedVideo.hash
                    ? `https://player.vimeo.com/video/${selectedVideo.id}?h=${selectedVideo.hash}&autoplay=1&title=0&byline=0&portrait=0&controls=1`
                    : `https://player.vimeo.com/video/${selectedVideo.id}?autoplay=1&title=0&byline=0&portrait=0&controls=1`
                }
                className={styles.videoIframe}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Vimeo Video"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReplaySessionsPage;
